import type { ProductFacetModel } from "@/api";
import type { ProductFacetTypeKey } from "@/types/product-facet";

/** Work graph node categories used for facet ↔ work mapping. */
export type FacetWorkTargetType =
  | "project"
  | "phase"
  | "deliverable"
  | "workPackage"
  | "workItem";

/**
 * Canonical Product Graph → Work Graph mapping.
 * Function → Project uses Work.productId (function facet id on project root).
 * All other rows use ProductFacetWork.
 */
export const FACET_TO_WORK_TARGET: Record<
  ProductFacetTypeKey,
  FacetWorkTargetType | null
> = {
  Product: null,
  Function: "project",
  JTBD: "deliverable",
  Experience: "workPackage",
  Capability: null,
  Feature: "workItem",
};

export const FACET_WORK_TARGET_LABEL: Record<FacetWorkTargetType, string> = {
  project: "Project",
  phase: "Phase",
  deliverable: "Deliverable",
  workPackage: "Work package",
  workItem: "Work item",
};

/** Facet types that support direct work linking UI (ProductFacetWork or project root). */
export function isFacetWorkLinkable(
  typeKey: ProductFacetTypeKey | null | undefined,
): boolean {
  if (!typeKey) return false;
  return FACET_TO_WORK_TARGET[typeKey] !== null;
}

/** Capability completion is derived from child Feature ↔ Work Item links. */
export function usesCapabilityRollup(
  typeKey: ProductFacetTypeKey | null | undefined,
): boolean {
  return typeKey === "Capability";
}

export function usesProjectRootLink(
  typeKey: ProductFacetTypeKey | null | undefined,
): boolean {
  return typeKey === "Function";
}

export function getWorkTargetForFacetType(
  typeKey: ProductFacetTypeKey,
): FacetWorkTargetType | null {
  return FACET_TO_WORK_TARGET[typeKey];
}

/** Classify a work type org profile name into a facet link target category. */
export function categorizeWorkTypeName(
  name: string | null | undefined,
): FacetWorkTargetType | null {
  const normalized = (name ?? "").trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("project")) return "project";
  if (normalized.includes("phase")) return "phase";
  if (normalized.includes("deliver")) return "deliverable";
  if (normalized.includes("package") || normalized.includes("pack")) {
    return "workPackage";
  }
  if (
    normalized.includes("item") ||
    normalized.includes("task") ||
    normalized.includes("story")
  ) {
    return "workItem";
  }
  return null;
}

export function collectFunctionFacetIds(
  facets: readonly ProductFacetModel[],
  productRootId: string,
  functionTypeId: string | undefined,
): Set<string> {
  if (!functionTypeId) return new Set();

  const ids = new Set<string>();
  for (const facet of facets) {
    if (
      facet.id &&
      facet.productFacetTypeId === functionTypeId &&
      facet.parentId === productRootId
    ) {
      ids.add(facet.id);
    }
  }
  return ids;
}

/** Walk parentId chain to find the Function facet id for a descendant facet. */
export function findFunctionFacetIdForFacet(
  facets: readonly ProductFacetModel[],
  facetId: string,
  functionTypeId: string | undefined,
): string | null {
  if (!functionTypeId) return null;

  const byId = new Map(
    facets.filter((f) => f.id).map((f) => [f.id as string, f]),
  );

  let current: ProductFacetModel | undefined = byId.get(facetId);
  while (current) {
    if (current.productFacetTypeId === functionTypeId && current.id) {
      return current.id;
    }
    const parentId = current.parentId;
    if (!parentId) return null;
    current = byId.get(parentId);
  }
  return null;
}

export function projectBelongsToProduct(
  projectFunctionFacetId: string | null | undefined,
  functionIdsForProduct: ReadonlySet<string>,
): boolean {
  if (!projectFunctionFacetId) return false;
  return functionIdsForProduct.has(projectFunctionFacetId);
}
